import { useEffect, useRef, useState } from "react";
import { Columns3, GripVertical, RotateCcw } from "lucide-react";

/**
 * ColumnChooser
 *
 * Dropdown button + popover that lets the user toggle column visibility and
 * drag-reorder columns. Wires up to `useColumnPrefs` via callbacks.
 *
 * Drag-reorder uses native HTML5 drag events on the chooser rows (not on the
 * table headers themselves) so the interaction is unambiguous and doesn't
 * interfere with cell clicks / row clicks.
 */
export default function ColumnChooser({
  orderedColumns,    // [{key, label, locked?}, ...]
  isVisible,         // (key) => boolean
  toggleColumn,      // (key) => void
  moveColumn,        // (fromKey, toKey) => void
  reset,             // () => void
  tableLabel = "table",
}) {
  const [open, setOpen] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const visibleCount = orderedColumns.filter((c) => isVisible(c.key)).length;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        data-testid={`column-chooser-btn-${tableLabel}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 h-10 px-3 bg-white text-zinc-700 text-sm font-semibold rounded-lg border border-zinc-300 hover:border-brand-500 hover:text-brand-700 transition-colors"
        title="Show / hide / reorder columns"
        aria-label="Columns"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Columns3 className="w-4 h-4" /> <span className="hidden sm:inline">Columns</span>
        <span className="font-mono text-[11px] text-zinc-400">{visibleCount}/{orderedColumns.length}</span>
      </button>

      {open && (
        <>
          {/* Mobile-only backdrop — tap to close. Desktop has the existing
              outside-click handler on popRef so no overlay needed there. */}
          <div
            className="fixed inset-0 bg-black/20 z-20 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={popRef}
            data-testid={`column-chooser-popover-${tableLabel}`}
            className={[
              // Mobile: render as a centered modal pinned to viewport so it
              // can never overflow off-screen. The button can be anywhere on
              // a narrow column and the chooser still fits.
              "fixed inset-x-3 top-16 max-h-[75vh] w-auto z-30 mx-auto max-w-sm",
              // Desktop (≥640px): revert to anchored popover to the right of the button.
              "sm:absolute sm:inset-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-64 sm:max-h-none sm:mx-0",
              "rounded-xl border border-zinc-200 bg-white shadow-xl overflow-hidden flex flex-col",
            ].join(" ")}
            role="dialog"
            aria-modal="true"
          >
          <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Columns</span>
            <button
              data-testid={`column-chooser-reset-${tableLabel}`}
              onClick={reset}
              className="text-[11px] text-zinc-500 hover:text-brand-700 font-semibold inline-flex items-center gap-1"
              title="Reset to defaults"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto py-1 flex-1 sm:flex-none">
            {orderedColumns.map((c) => {
              const visible = isVisible(c.key);
              const draggable = !c.locked;
              return (
                <div
                  key={c.key}
                  draggable={draggable}
                  onDragStart={(e) => {
                    if (!draggable) return;
                    setDragKey(c.key);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => { if (draggable && dragKey && dragKey !== c.key) e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragKey && dragKey !== c.key) moveColumn(dragKey, c.key);
                    setDragKey(null);
                  }}
                  onDragEnd={() => setDragKey(null)}
                  className={[
                    "flex items-center gap-2 px-3 py-1.5 text-sm select-none",
                    draggable ? "cursor-grab active:cursor-grabbing hover:bg-zinc-50" : "opacity-60",
                    dragKey === c.key ? "bg-brand-50" : "",
                  ].join(" ")}
                  data-testid={`column-chooser-row-${tableLabel}-${c.key}`}
                >
                  <GripVertical className={`w-3.5 h-3.5 ${draggable ? "text-zinc-400" : "text-zinc-200"}`} />
                  <input
                    type="checkbox"
                    checked={visible}
                    disabled={c.locked}
                    onChange={() => toggleColumn(c.key)}
                    data-testid={`column-chooser-toggle-${tableLabel}-${c.key}`}
                  />
                  <span className="flex-1 truncate">{c.label}</span>
                  {c.locked && <span className="text-[10px] text-zinc-400 font-mono">locked</span>}
                </div>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-zinc-100 text-[11px] text-zinc-500">
            Drag rows to reorder. Choices are saved on this device.
          </div>
          </div>
        </>
      )}
    </div>
  );
}
